import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import Button from "@ats/src/components/shared/Button";
import ConfirmationModal from "@ats/src/components/modals/ConfirmationModal";
import Icon from "@ats/src/components/shared/Icon";

import { useModalContext } from "@shared/context/ModalContext";
import { useToastContext } from "@shared/context/ToastContext";

import {
  useCreatePaidBoardWhatJobsListing,
  usePauseBoardWhatJobsListing,
  useActivateBoardWhatJobsListing,
  useCreateWhatJobsCheckoutSession,
} from "@shared/queryHooks/useWhatJobsListing";

interface WhatJobsSidebarActionsProps {
  isLive: boolean;
  isExpired: boolean;
  isMissingAnyRequirements: boolean;
  jobId: number;
  currentListing: any;
  listingData: any;
  hasPaymentMethod: boolean;
  isPurchasing: boolean;
  isPaidButProcessing: boolean;
  setIsPurchasing: (value: boolean) => void;
  setErrors: (errors: any) => void;
  setIsDirty: (value: boolean) => void;
  isAdmin: boolean;
}

function WhatJobsSidebarActions(props: WhatJobsSidebarActionsProps) {
  const {
    isLive,
    isExpired,
    isMissingAnyRequirements,
    jobId,
    currentListing,
    listingData,
    hasPaymentMethod,
    setIsPurchasing,
    setErrors,
    setIsDirty,
    isPurchasing,
    isPaidButProcessing,
    isAdmin,
  } = props;

  const addToast = useToastContext();
  const { openModal, removeModal } = useModalContext();

  const {
    mutate: createPaidBoardWhatJobsListing,
    isLoading: isLoadingCreatePaidBoardWhatJobsListing,
  } = useCreatePaidBoardWhatJobsListing();
  const {
    mutate: pauseBoardWhatJobsListing,
    isLoading: isLoadingPauseBoardWhatJobsListing,
  } = usePauseBoardWhatJobsListing();
  const {
    mutate: activateBoardWhatJobsListing,
    isLoading: isLoadingActivateBoardWhatJobsListing,
  } = useActivateBoardWhatJobsListing();
  const {
    mutate: createCheckoutSession,
    isLoading: isLoadingCreateCheckoutSession,
  } = useCreateWhatJobsCheckoutSession();

  const isLoading =
    isLoadingCreatePaidBoardWhatJobsListing ||
    isLoadingPauseBoardWhatJobsListing ||
    isLoadingActivateBoardWhatJobsListing ||
    isLoadingCreateCheckoutSession;

  const handlePurchase = () => {
    if (isMissingAnyRequirements) {
      addToast({ title: "Please complete all required fields before purchasing", kind: "warning" });
      return;
    }
    // Set purchasing state IMMEDIATELY to prevent race condition
    setIsPurchasing(true);
    if (hasPaymentMethod) {
      // Direct charge
      handleCreatePaidBoardWhatJobsListing();
    } else {
      // Checkout session
      handleCreateCheckoutSession();
    }
  };

  const handleCreatePaidBoardWhatJobsListing = () => {
    createPaidBoardWhatJobsListing(
      {
        jobId: jobId,
        boardWhatJobsListing: listingData,
      },
      {
        onSuccess: () => {
          window.logger(
            "%c[WhatJobsSidebarActions] handleCreatePaidBoardWhatJobsListing onSuccess\n\n\n\n",
            "background-color: #5eb65b",
            {
              jobId,
              listingData,
            },
          );
          // Don't reset isPurchasing here - let the useEffect in parent handle it
          // This prevents button flicker between API success and query refetch
          setIsDirty(false);
        },
        onError: (response: { data: any }) => {
          setIsPurchasing(false);
          setErrors(response.data.errors);
          addToast({
            title: response.data.errors?.general?.[0] || "Failed to purchase listing",
            kind: "error",
          });
        },
      },
    );
  };

  const handleCreateCheckoutSession = () => {
    createCheckoutSession(
      {
        jobId: jobId,
        boardWhatJobsListing: listingData,
      },
      {
        onSuccess: (data) => {
          window.logger(
            "%c[WhatJobsSidebarActions] handleCreateCheckoutSession onSuccess\n\n\n\n",
            "background-color: #5eb65b",
            {
              jobId,
              listingData,
              checkoutUrl: data.url,
            },
          );
          // Redirect to Stripe Checkout
          window.location.href = data.url;
        },
        onError: (response: { data: any }) => {
          setIsPurchasing(false);
          setErrors(response.data.errors);
          addToast({
            title: response.data.errors?.general?.[0] || "Failed to create checkout session",
            kind: "error",
          });
        },
      },
    );
  };

  const handleActivate = () => {
    activateBoardWhatJobsListing(
      { jobId: jobId, id: currentListing.id },
      {
        onSuccess: () => {
          window.logger(
            "%c[WhatJobsSidebarActions] handleActivate onSuccess\n\n\n\n",
            "background-color: #5eb65b",
            {
              jobId,
              listingId: currentListing.id,
            },
          );
        },
        onError: (response: { data: any }) => {
          setErrors(response.data.errors);
          addToast({
            title: response.data.errors?.general?.[0] || "Failed to activate listing",
            kind: "error",
          });
        },
      },
    );
  };

  const handlePauseWhatJobsListing = () => {
    pauseBoardWhatJobsListing(
      { jobId: jobId, id: currentListing.id },
      {
        onSuccess: () => {
          window.logger(
            "%c[WhatJobsSidebarActions] handlePauseWhatJobsListing onSuccess\n\n\n\n",
            "background-color: #5eb65b",
            {
              jobId,
              listingId: currentListing.id,
            },
          );
          removeModal();
          // Query invalidation handled by hook
        },
        onError: (response: { data: any }) => {
          removeModal();
          setErrors(response.data.errors);
          addToast({
            title: response.data.errors?.general?.[0] || "Failed to pause listing",
            kind: "error",
          });
        },
      },
    );
  };

  const handleClickOnPause = () => {
    const modal = (
      <ConfirmationModal
        title="Are you sure you want to pause your WhatJobs premium distribution?"
        subcopy="This will pause your listing on WhatJobs and its partner network. You can reactivate it later."
        confirmText="Pause distribution"
        isDestructiveConfirm={true}
        cancelText="Keep distribution active"
        onConfirm={() => {
          handlePauseWhatJobsListing();
        }}
        onCancel={() => {
          removeModal();
        }}
      />
    );

    openModal(modal);
  };

  if (isLive || isPaidButProcessing) {
    // Should we remove all pause/activate functions from frontend before QA? //   </Button> //     Activate post //   <Button styleType="secondary" onClick={handleActivate} loading={isLoading}> // ) : (currentListing?.status === "paused" && !isExpired) ? ( // </Button> //   Pause post // <Button styleType="secondary" onClick={handleClickOnPause} loading={isLoading}>
    return null;
  } else if (!isAdmin) {
    return (
      <Styled.AdminRequiredMessage>
        <Icon name="alert-circle" />
        Only admin users can purchase a listing.
      </Styled.AdminRequiredMessage>
    );
  } else {
    return (
      <Button
        onClick={handlePurchase}
        disabled={isMissingAnyRequirements || isLoading || isPurchasing}
        loading={isLoading || isPurchasing}
      >
        Purchase now
      </Button>
    );
  }
}

export default WhatJobsSidebarActions;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.AdminRequiredMessage = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: WhatJobsSidebarActions_AdminRequiredMessage;
    ${[t.p(4), t.rounded.md, t.mt(4)]}
    border: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : t.color.gray[100]};
    color: ${t.dark ? t.color.gray[200] : t.color.gray[600]};
    ${[t.text.xs]}
    line-height: normal;
    display: flex;
    align-items: center;
    font-weight: 500;

    svg {
      flex-shrink: 0;
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
      ${[t.mr(2)]}
    }
  `;
});
